/** @prettier */

import {useState} from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Stack,
  IconButton,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {startOfMonth, subDays} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {useParams} from 'react-router-dom';

//MTH component import
import {dayOfWeekTable, monthTable} from './ScheduleSetting';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {SelectTypeMenu} from './SelectTypeMenuList';
import {iconBlack} from '../../define';

//API import
import {putSiteCalender} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

/** カレンダー１マス毎に保管するデータ型 */
type calenderCellData = {
  date: number;
  month: number;
  dayType: 'weekday' | 'holiday' | 'special-day-1' | 'special-day-2';
  enabled: boolean;
};

/**
 * 年間スケジュール編集ダイアログ
 * @param props props
 * - timeZone タイムゾーン
 * - calender 日毎の特定日
 * - calenderRoutine スケジュールの標準設定
 * - isOpenDialog 年間スケジュール編集ダイアログ開閉
 * - setIsOpenDialog 年間スケジュール編集ダイアログ開閉設定
 * - setHasCalenderInfoChanged スケジュール画面更新フラグ
 */
export const CalenderEditDialog = (props: {
  timeZone: string;
  calender: apiIF.res.getSiteCalender;
  calenderRoutine: apiIF.res.getSiteCalenderRoutine;
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setHasCalenderInfoChanged: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  /** 編集前の日毎の特定日 */
  const copyCalenderData = JSON.parse(JSON.stringify(props.calender));
  /** 編集後の日毎の特定日 */
  const [calenderData, setCalenderData] = useState(copyCalenderData);
  /** 特定日設定メニュー開閉 */
  const [menuOpen, setMenuOpen] = useState(false);
  /** 特定日設定メニュー表示位置 */
  const [menuPosition, setMenuPosition] = useState(null);
  /** 特定日を編集する月 */
  const [editMonth, setEditMonth] = useState<number>(0);
  /** 特定日の設定データ */
  const [editDate, setEditDate] = useState<number>(0);

  /** 今日 */
  const today = utcToZonedTime(new Date(), props.timeZone);
  /** カレンダー作成用の曜日（初期値は今月の初日の曜日） */
  let calenderDayOfWeek: number = startOfMonth(today).getDay();
  /** ７マス分（１週間）のデータ */
  let oneWeekCellData: calenderCellData[] = [];
  //年間スケジュールカレンダー作成
  //初日の曜日まで前月の日で埋める
  while (calenderDayOfWeek !== 1) {
    //カレンダーが月曜日スタートのため、日曜日の場合は土曜日の一つ後ろとして扱う
    if (calenderDayOfWeek === 0) {
      calenderDayOfWeek = 7;
    }
    /** 今月の月初 */
    const startMonth = startOfMonth(today);

    /** カレンダー１マス分のデータ
     * @param date カレンダーの対象マスの日にち
     * @param month カレンダーの対象マスの月
     * @param dayType カレンダーの対象マスの特定日タイプ
     * @param enabled カレンダーの対象マスの編集有効無効
     */
    const oneDayCellData: calenderCellData = {
      date: subDays(startMonth, calenderDayOfWeek - 1).getDate(),
      month: subDays(startMonth, 1).getMonth(),
      dayType: 'weekday',
      enabled: false,
    };
    //７マス分（１週間）のデータ配列に格納
    oneWeekCellData.push(oneDayCellData);
    calenderDayOfWeek--;
  }

  /** (７日*５４週)マス分(約１年)のデータ */
  const oneYearCellData: calenderCellData[][] = [[]];
  /** 月（初期値は今月） */
  let month: number = today.getMonth();
  /** １カ月の日数カウント */
  let countDate: number = 0;
  //約１年(５４週)分のカレンダーを作成する
  for (let yearOfWeek = 0; yearOfWeek < 54; yearOfWeek++) {
    for (let weekOfDay = 0; weekOfDay < 7; weekOfDay++) {
      if (yearOfWeek === 0) {
        //初日の曜日までは前月の日で埋まっているため、そこまでインデックスを進める
        weekOfDay = oneWeekCellData.length;
      }
      /** 対象のカレンダーマスの編集有効無効（１年後の同月は編集無効にする） */
      const cellEnabled = yearOfWeek < 52 || month !== today.getMonth();

      /** カレンダー１マス分のデータ
       * @param date カレンダーの対象マスの日にち
       * @param month カレンダーの対象マスの月
       * @param dayType カレンダーの対象マスの特定日タイプ
       * @param enabled カレンダーの対象マスの編集有効無効
       */
      const oneDayCellData = {
        date: countDate + 1,
        month: month,
        dayType: calenderData[monthTable[month]][countDate],
        enabled: cellEnabled,
      };
      //７マス分（１週間）のデータ配列に格納
      oneWeekCellData.push(oneDayCellData);
      //１カ月の日数カウント＋１
      countDate++;
      if (countDate >= calenderData[monthTable[month]].length) {
        //１カ月の日数カウントが月毎の日数を超えたら次月にする
        month++;
        countDate = 0;
        if (month > 11) {
          //１２月を超えたら１月に戻す
          month = 0;
        }
      }
    }
    //１週間分のデータ配列を１年分のデータ配列に格納
    oneYearCellData.push([...oneWeekCellData]);
    //１週間分のデータ配列を初期化
    oneWeekCellData = [];
  }

  // 画面左上に今見ている月を表示する。非常に動作が重かったので要改修
  // おそらく毎回レンダリングしているから。マウスホイールでしか動作しない
  // ためタブレット等に対応させる必要もある
  // const [monthText,setMonthText] = useState(month + 1 + "月");

  // let anchor1: HTMLElement;
  // let anchor_domrect1: DOMRect;
  // let table: HTMLElement;
  // let table_domrect:DOMRect;
  // let nowAnchorPoint = document.getElementById("target1");

  // const [startPos,setStartPos] = useState<number>();
  // const [monthHeight, setMonthHeight] = useState<number>(0);
  // const getStart = () => {
  //     anchor1 = document.getElementById("target1")!;
  //     anchor_domrect1 = anchor1?.getBoundingClientRect();
  //     table = document.getElementById("target3")!;
  //     table_domrect = table?.getBoundingClientRect();
  //     setStartPos(anchor_domrect1!.y);
  //     setMonthHeight(table_domrect!.height / 12);
  // }
  // useEffect(getStart,[]);

  // const changeMonthText = () => {
  //     let nowAnchorPoint_domrect = nowAnchorPoint?.getBoundingClientRect();
  //     let tempmonth = 1;
  //     for(let i = 0; i < 12;i++){
  //         if(startPos! / 2 - (monthHeight * i) < nowAnchorPoint_domrect!.y){
  //             tempmonth += i;
  //             break;
  //         }
  //     }
  //     if(12 < month + tempmonth){
  //         tempmonth -= 12
  //     }
  //     if(monthText !== (tempmonth +month).toString() + "月"){
  //         setMonthText((tempmonth +month).toString() + "月");
  //     }
  // };

  // //document.onwheel = () => changeMonthText();
  // const monthTitle = useMemo(() => (monthText), [monthText])

  /** 標準設定情報取り込み */
  const calenderRoutineImport = () => {
    /** 標準設定読み込み時用の日毎の特定日 */
    let tempCalenderData = copyCalenderData;
    /** 月（初期値は今月） */
    let month: number = today.getMonth();
    /** 標準設定取り込み用の曜日（初期値は今月の初日の曜日） */
    let routineDayOfWeek = startOfMonth(today).getDay();
    //週毎の特定日設定を読み込む
    for (let numberOfLoop = 0; numberOfLoop < 12; numberOfLoop++) {
      //12か月分ループ
      for (let date in copyCalenderData[monthTable[month]]) {
        switch (routineDayOfWeek) {
          case 0: //日曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.sunday;
            break;
          case 1: //月曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.monday;
            break;
          case 2: //火曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.tuesday;
            break;
          case 3: //水曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.wednesday;
            break;
          case 4: //木曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.thursday;
            break;
          case 5: //金曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.friday;
            break;
          case 6: //土曜
            tempCalenderData[monthTable[month]][date] =
              props.calenderRoutine.weekly.saturday;
            break;
        }
        routineDayOfWeek++;
        if (routineDayOfWeek > 6) {
          //日曜日にリセット
          routineDayOfWeek = 0;
        }
      }
      month++;
      if (11 < month) {
        //１月にリセット
        month = 0;
      }
    }

    //標準設定をカレンダーに設定する順番に入れ替えるために分ける
    /** 週番号'last'を指定している特別週間設定一覧 */
    const lastWeekNumberRoutine = props.calenderRoutine['specified-day'].filter(
      item => {
        return 'month' in item && item['week-number'] === 'last';
      }
    );
    /** 週番号'last'以外を指定した特別週間設定一覧 */
    const weekNumberRoutine = props.calenderRoutine['specified-day'].filter(
      item => {
        return 'month' in item && item['week-number'] !== 'last';
      }
    );
    /** 特別日設定一覧 */
    const dateRoutine = props.calenderRoutine['specified-day'].filter(item => {
      return 'date' in item;
    });
    /** 読み込む順番に入れ替えた標準設定（最終周指定 < 最終週以外の週指定 < 日付指定）*/
    const sortCalenderRoutine = lastWeekNumberRoutine
      .concat(weekNumberRoutine)
      .concat(dateRoutine);

    //特別日の読み込み
    for (let specifiedDayInfo of sortCalenderRoutine) {
      //月、週、曜日で指定した特別日の読み込み
      if ('month' in specifiedDayInfo) {
        /* 特別日の曜日 （０：月曜日～６：日曜日）*/
        let dayOfWeekOfSpecifiedDay = 0;
        //特別日の曜日のインデックスを取得
        switch (specifiedDayInfo['day-of-week']) {
          case 'monday':
            dayOfWeekOfSpecifiedDay = 0;
            break;
          case 'tuesday':
            dayOfWeekOfSpecifiedDay = 1;
            break;
          case 'wednesday':
            dayOfWeekOfSpecifiedDay = 2;
            break;
          case 'thursday':
            dayOfWeekOfSpecifiedDay = 3;
            break;
          case 'friday':
            dayOfWeekOfSpecifiedDay = 4;
            break;
          case 'saturday':
            dayOfWeekOfSpecifiedDay = 5;
            break;
          case 'sunday':
            dayOfWeekOfSpecifiedDay = 6;
            break;
        }

        /* 特別日の月 */
        const specifiedDayOfMonth = monthTable.indexOf(
          specifiedDayInfo['month']
        );
        /* 年（初期値は今年）*/
        let year: number = today.getFullYear();
        //特別日の月が今月より小さい場合は年を１つ増やす
        if (specifiedDayOfMonth < today.getMonth()) {
          year++;
        }
        /* 特別日の月の初日の曜日 */
        let dayOfWeekOnFirstDayOfMonth = new Date(
          year,
          specifiedDayOfMonth,
          1
        ).getDay();
        //特別日の月の初日の曜日を０：月曜日～６：日曜日となるように調整
        if (dayOfWeekOnFirstDayOfMonth !== 0) {
          dayOfWeekOnFirstDayOfMonth = dayOfWeekOnFirstDayOfMonth - 1;
        }
        //日曜日の場合は個別に指定
        else {
          dayOfWeekOnFirstDayOfMonth = 6;
        }

        /* 特別日の週数（第何曜日目） */
        let weekNumber = 0;
        /* 特別日の日にち */
        let specifiedDayOfDate = 0;

        //特別日の週（第何曜日目）を取得する
        switch (specifiedDayInfo['week-number']) {
          case 'first':
          default:
            if (dayOfWeekOfSpecifiedDay >= dayOfWeekOnFirstDayOfMonth) {
              //特別日の曜日が特別日の月の初日の曜日より前の場合は１週目の週カウントは０とする
              weekNumber = 0;
            } else {
              //特別日の曜日が特別日の月の初日の曜日より後の場合は１週目の週カウントは１とする
              weekNumber = 1;
            }
            //特別日を計算する
            specifiedDayOfDate =
              7 * weekNumber +
              dayOfWeekOfSpecifiedDay -
              dayOfWeekOnFirstDayOfMonth +
              1;
            break;
          case 'second':
            if (dayOfWeekOfSpecifiedDay >= dayOfWeekOnFirstDayOfMonth) {
              weekNumber = 1;
            } else {
              weekNumber = 2;
            }
            specifiedDayOfDate =
              7 * weekNumber +
              dayOfWeekOfSpecifiedDay -
              dayOfWeekOnFirstDayOfMonth +
              1;
            break;
          case 'third':
            if (dayOfWeekOfSpecifiedDay >= dayOfWeekOnFirstDayOfMonth) {
              weekNumber = 2;
            } else {
              weekNumber = 3;
            }
            specifiedDayOfDate =
              7 * weekNumber +
              dayOfWeekOfSpecifiedDay -
              dayOfWeekOnFirstDayOfMonth +
              1;
            break;
          case 'fourth':
            if (dayOfWeekOfSpecifiedDay >= dayOfWeekOnFirstDayOfMonth) {
              weekNumber = 3;
            } else {
              weekNumber = 4;
            }
            specifiedDayOfDate =
              7 * weekNumber +
              dayOfWeekOfSpecifiedDay -
              dayOfWeekOnFirstDayOfMonth +
              1;
            break;
          case 'fifth':
            if (dayOfWeekOfSpecifiedDay >= dayOfWeekOnFirstDayOfMonth) {
              weekNumber = 4;
            } else {
              weekNumber = 5;
            }
            specifiedDayOfDate =
              7 * weekNumber +
              dayOfWeekOfSpecifiedDay -
              dayOfWeekOnFirstDayOfMonth +
              1;
            break;
          case 'last':
            //最終週の曜日の日は月の日数ー７のいずれか
            for (let i = 0; i < 7; i++) {
              //最終周の日にち
              const dateOfLastWeek =
                tempCalenderData[monthTable[specifiedDayOfMonth]].length - i;
              //最終周の曜日
              let dayOfWeekOfLastWeek = new Date(
                year,
                specifiedDayOfMonth,
                tempCalenderData[monthTable[specifiedDayOfMonth]].length - i
              ).getDay();
              if (dayOfWeekOfLastWeek === 0) {
                //月曜始まりにする
                dayOfWeekOfLastWeek = 6;
              } else {
                dayOfWeekOfLastWeek = dayOfWeekOfLastWeek - 1;
              }

              if (dayOfWeekOfSpecifiedDay === dayOfWeekOfLastWeek) {
                specifiedDayOfDate = dateOfLastWeek;
              }
            }
            break;
        }

        if (
          specifiedDayOfDate <=
          tempCalenderData[monthTable[specifiedDayOfMonth]].length
        ) {
          //特別日が月の日数を超えていなければ日毎の特定日に特別日を設定する
          tempCalenderData[monthTable[specifiedDayOfMonth]][
            specifiedDayOfDate - 1
          ] = specifiedDayInfo['schedule'];
        } else {
          //特別日が月の日数を超えていた場合は何もしない
        }
      }
      //日時を指定した特別日の読み込み
      else if ('date' in specifiedDayInfo) {
        //特定日の月を文字列から数値に変換
        const specifiedDayOfMonth = parseInt(
          specifiedDayInfo['date'].substring(0, 2)
        );
        //特定日の日を文字列から数値に変換
        const specifiedDayOfDate = parseInt(
          specifiedDayInfo['date'].substring(3)
        );
        //標準設定用日毎の特定日に特別日を設定する
        tempCalenderData[monthTable[specifiedDayOfMonth - 1]][
          specifiedDayOfDate - 1
        ] = specifiedDayInfo['schedule'];
      }
    }
    //日毎の特定日に標準設定用日毎の特定日を設定する
    setCalenderData(tempCalenderData);
  };

  /** OKボタン押下時に編集した特定日カレンダーをDBに保存する */
  const updateCalenderDate = () => {
    if (siteID === undefined) return;
    try {
      //特定日カレンダの修正
      const promise = putSiteCalender(siteID, calenderData);
      promise.then(async () => {
        props.setHasCalenderInfoChanged(true);
      });
    } catch {
      props.setHasCalenderInfoChanged(false);
    }
    props.setIsOpenDialog(false);
  };

  /** キャンセルボタン押下時に編集した特定日カレンダーを元に戻す */
  const cancelCalenderDate = () => {
    setCalenderData(copyCalenderData);
    props.setIsOpenDialog(false);
  };

  /** 日毎の特定日編集メニュー表示ボタン押下時の処理
   * @param event マウスクリックイベント
   * @param date 編集する日
   * @param month 編集する月
   */
  const handleClick = (
    event: React.MouseEvent,
    date: number,
    month: number
  ) => {
    setMenuOpen(true);
    setMenuPosition(
      event.currentTarget as unknown as React.SetStateAction<null>
    );
    setEditDate(date - 1);
    setEditMonth(month);
  };

  /** 日毎の特定日編集メニューアイテム押下時の処理
   * @param num 選択した特定日タイプメニューのインデックス
   */
  const clickMenuItem = (num: number = -1) => {
    setMenuOpen(false);
    if (num === -1) {
      // 変更内容を指定しなかった場合は変更せず終了する
      return;
    }
    switch (num) {
      case 0:
        calenderData[monthTable[editMonth]][editDate] = 'weekday';
        break;
      case 1:
        calenderData[monthTable[editMonth]][editDate] = 'holiday';
        break;
      case 2:
        calenderData[monthTable[editMonth]][editDate] = 'special-day-1';
        break;
      case 3:
        calenderData[monthTable[editMonth]][editDate] = 'special-day-2';
        break;
    }
  };

  /**
   * カレンダーセルの中身、ボタン以外（ボタンをこの関数に含めると特定日設定メニューの表示される位置がおかしくなる）
   * @param props
   * - date 日にち
   * - month 月名
   * @returns JSX
   */
  const CalenderCell = (props: {date: number; month: number}) => {
    /** 月名（省略形）一覧 */
    const monthAbbreviationTable = [
      trans('CalenderEditDialog.monthAbbreviationTable.january'),
      trans('CalenderEditDialog.monthAbbreviationTable.february'),
      trans('CalenderEditDialog.monthAbbreviationTable.march'),
      trans('CalenderEditDialog.monthAbbreviationTable.april'),
      trans('CalenderEditDialog.monthAbbreviationTable.may'),
      trans('CalenderEditDialog.monthAbbreviationTable.june'),
      trans('CalenderEditDialog.monthAbbreviationTable.july'),
      trans('CalenderEditDialog.monthAbbreviationTable.august'),
      trans('CalenderEditDialog.monthAbbreviationTable.september'),
      trans('CalenderEditDialog.monthAbbreviationTable.october'),
      trans('CalenderEditDialog.monthAbbreviationTable.november'),
      trans('CalenderEditDialog.monthAbbreviationTable.december'),
    ];

    let dateText: string = props.date.toString();
    if (props.date === 1) {
      dateText = trans('CalenderEditDialog.date', {
        date: dateText,
        month: monthAbbreviationTable[props.month],
      });
    }
    return <Box>{dateText}</Box>;
  };

  return (
    <Dialog
      open={props.isOpenDialog}
      onClose={() => props.setIsOpenDialog(false)}
      maxWidth="md">
      <DialogTitle>{trans('CalenderEditDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/** 標準設定読み込みボタン */}
          <Box className="schedule__calenderEditDialog__defaultImport">
            <PrimaryButton
              onClick={() => calenderRoutineImport()}
              buttonText={trans('CalenderEditDialog.standard')}
              className="schedule__calenderEditDialog__defaultImport__button"
            />
          </Box>
          {/** カレンダー */}
          <Box className="schedule__calenderEditDialog__calender">
            <table className="schedule__calenderEditDialog__calender__table">
              <thead>
                <tr>
                  {dayOfWeekTable.map(value => {
                    return (
                      <th
                        key={value}
                        className="schedule__calenderEditDialog__calender__table__header__cell">
                        {trans(`CalenderEditDialog.dayOfWeekTable.${value}`)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {oneYearCellData.map((weekdayCellData, index) => {
                  return (
                    <tr key={index}>
                      {weekdayCellData.map(value => {
                        //編集ができない日付の場合
                        if (value.enabled === false) {
                          return (
                            <td
                              key={`${value.month}-${value.date}`}
                              className="schedule__calenderEditDialog__calender__table__body__cell--notTargetEdit">
                              <Stack alignItems="center">
                                <CalenderCell
                                  date={value.date}
                                  month={value.month}
                                />
                                <IconButton disabled>
                                  <img
                                    src={iconBlack.CHEVRON_DOWN.src}
                                    className="schedule__calenderEditDialog__calender__table__body__cell--notTargetEditIcon"
                                    alt={`${trans(
                                      `CalenderEditDialog.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                                    )}`}
                                  />
                                </IconButton>
                              </Stack>
                            </td>
                          );
                        }

                        /** 適用するクラス名 */
                        const className = ((
                          dayType:
                            | 'weekday'
                            | 'holiday'
                            | 'special-day-1'
                            | 'special-day-2'
                        ) => {
                          switch (dayType) {
                            case 'weekday':
                              return 'schedule__calenderEditDialog__calender__table__body__cell--weekday';
                            case 'holiday':
                              return 'schedule__calenderEditDialog__calender__table__body__cell--holiday';
                            case 'special-day-1':
                              return 'schedule__calenderEditDialog__calender__table__body__cell--specialDay1';
                            case 'special-day-2':
                              return 'schedule__calenderEditDialog__calender__table__body__cell--specialDay2';
                          }
                        })(value.dayType);
                        return (
                          <td
                            key={`${value.month}-${value.date}`}
                            className={className}>
                            <Stack alignItems="center">
                              <CalenderCell
                                date={value.date}
                                month={value.month}
                              />
                              <IconButton
                                onClick={e =>
                                  handleClick(e, value.date, value.month)
                                }>
                                <img
                                  src={iconBlack.CHEVRON_DOWN.src}
                                  alt={`${trans(
                                    `CalenderEditDialog.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                                  )}`}
                                />
                              </IconButton>
                            </Stack>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Stack>
        {/* 設定変更用メニュー */}
        <SelectTypeMenu
          id="weekDay-setting-menu"
          menuPosition={menuPosition}
          open={menuOpen}
          clickMenuItem={clickMenuItem}
        />
      </DialogContent>
      <DialogActions>
        {/** CANCEL button */}
        <SecondaryButton
          onClick={cancelCalenderDate}
          buttonText={trans('CalenderEditDialog.buttonTxt.cancel')}
        />
        {/** OK button */}
        <PrimaryButton
          onClick={updateCalenderDate}
          buttonText={trans('CalenderEditDialog.buttonTxt.ok')}
        />
      </DialogActions>
    </Dialog>
  );
};
